import { FC, useContext, useEffect } from 'react'
import { FormattedMessage } from 'react-intl'
import { generatePath, Link, useNavigate, useParams } from 'react-router'

import { Flex, Table, Typography } from 'antd'
import { useResponsive } from 'antd-style'
import { ColumnProps } from 'antd/es/table'

import { useQuery } from '@tanstack/react-query'
import slugify from 'slugify'

import { Country } from '../../components/icon/Country'
import routes, { generateSlug } from '../../routes'
import { client } from '../../services/motogp'
import { RiderNumber } from '../riders/RiderNumber'

import { Context } from './Context'

const ResultTable: FC<{ sessionId: string }> = ({ sessionId }) => {
  // const ridersResults = useRidersQuery({ variables: { pageSize: 1000 } })
  // const trackResults = useTracksQuery()
  // const bikesResults = useBikesQuery()
  // const [createResult] = useCreateResultMutation()
  // const [createRider] = useCreateRiderMutation()
  const { state } = useContext(Context)
  const { mobile } = useResponsive()
  const params = useParams()
  const navigate = useNavigate()
  const { isLoading, data } = useQuery({
    queryKey: ['session', sessionId],
    queryFn: () => client.classification(sessionId, state.test),
  })

  useEffect(() => {
    if (state.params.year && state.params.category && state.params.track) {
      if (state.params.track !== params.track || state.params.category !== params.category) {
        navigate(
          generatePath(routes.result, {
            year: state.params.year,
            category: state.params.category,
            track: state.params.track,
          }),
        )
      }
    }
  }, [state.params.year, state.params.track, state.params.category, navigate, params])

  const bikes: Record<string, string> = (data ?? [])
    .map((it) => it.constructor)
    .reduce((acc, cur) => ({ ...acc, ...{ [cur.id]: cur?.name } }), {})
  const teams: Record<string, string> = (data ?? [])
    .map((it) => it.team)
    .filter(Boolean)
    .reduce((acc, cur) => ({ ...acc, ...{ [cur?.name]: cur?.name } }), {})

  // const year = 1949
  // const saveData = useCallback(() => {
  //   data?.forEach((it) => {
  //     const rider = ridersResults.data?.riders.find(
  //       (rider) => rider?.externalId === `${it.rider.legacy_id}`,
  //     )?.documentId
  //     const track = trackResults.data?.tracks.find((track) => track?.externalUuid === state.circuitId)?.documentId
  //     const bike = bikesResults.data?.bikes.find((bike) =>
  //       bike?.externalUuid === it?.constructor?.id ? it?.constructor?.id : null,
  //     )?.documentId
  //     if (!rider) {
  //       createRider({
  //         variables: {
  //           data: {
  //             externalId: `${it.rider.legacy_id}`,
  //             name: it.rider.full_name,
  //             externalUuid: it.rider.riders_api_uuid,
  //             slug: generateSlug(it.rider.full_name),
  //           },
  //         },
  //       })
  //         .then((data) => data.data?.createRider?.documentId)
  //         .then((rider) => {
  //           const data: ResultInput = {
  //             didNotFinish: it.status === 'OUTSTND',
  //             avgSpeed: it.average_speed,
  //             rider,
  //             track,
  //             bike,
  //             laps: it.total_laps,
  //             position: it.position,
  //             gap: Number.parseFloat(it.gap.first),
  //             year,
  //             points: it.points,
  //             type: Enum_Result_Type.Rac,
  //             racingClass: Enum_Result_Racingclass.Motogp,
  //           }
  //
  //           createResult({
  //             variables: {
  //               data,
  //             },
  //           }).then(console.log)
  //         })
  //     } else {
  //       const data: ResultInput = {
  //         didNotFinish: it.status === 'OUTSTND',
  //         avgSpeed: it.average_speed,
  //         rider,
  //         track,
  //         bike,
  //         laps: it.total_laps,
  //         position: it.position,
  //         gap: Number.parseFloat(it.gap.first),
  //         year,
  //         points: it.points,
  //         type: Enum_Result_Type.Rac,
  //         racingClass: Enum_Result_Racingclass.Motogp,
  //       }
  //
  //       createResult({
  //         variables: {
  //           data,
  //         },
  //       }).then(console.log)
  //     }
  //   })
  // }, [
  //   data,
  //   ridersResults.data?.riders,
  //   trackResults.data?.tracks,
  //   bikesResults.data?.bikes,
  //   state.circuitId,
  //   createRider,
  //   createResult,
  // ])
  //
  // if (ridersResults.loading || trackResults.loading || bikesResults.loading || isLoading) return null

  const columnsDesktop: ColumnProps<MotoGP.Result>[] = [
    {
      key: 'results.table.position',
      title: <FormattedMessage id={'results.table.position'} />,
      dataIndex: ['position'],
      width: 40,
      align: 'center',
      fixed: 'left',
    },
    {
      key: 'results.table.rider',
      title: <FormattedMessage id={'results.table.rider'} />,
      dataIndex: ['rider'],
      render: (it: MotoGP.Result['rider']) => (
        <Flex gap={4} align={'center'}>
          <RiderNumber number={it.number} />
          <Link to={generatePath(routes.rider, { slug: generateSlug(it.full_name) })}>{it.full_name}</Link>
        </Flex>
      ),
      width: 250,
    },
    {
      key: 'rider.country',
      title: <FormattedMessage id={'rider.country'} />,
      dataIndex: ['rider', 'country'],
      align: 'center',
      width: 50,
      render: (it: MotoGP.Result['rider']['country']) => <Country country={it?.iso} />,
    },
    {
      key: 'results.table.team',
      title: <FormattedMessage id={'results.table.team'} />,
      dataIndex: ['team', 'name'],
      render: (it: MotoGP.Result['team']['name']) => <Typography.Text ellipsis>{it}</Typography.Text>,
      filters: Object.entries(teams).map(([value, text]) => ({
        text,
        value,
      })),
      onFilter: (value, record: MotoGP.Result) => record.team.name === value,
      filterSearch: true,
    },
    {
      key: 'results.table.constructor',
      title: <FormattedMessage id={'results.table.constructor'} />,
      dataIndex: ['constructor', 'name'],
      filters: Object.entries(bikes).map(([value, text]) => ({
        text,
        value,
      })),
      filterSearch: true,
      onFilter: (value, record: MotoGP.Result) => record.constructor.id === value,
    },
    {
      key: 'results.table.laps',
      title: <FormattedMessage id={'results.table.laps'} />,
      dataIndex: ['total_laps'],
      align: 'center',
    },
    {
      key: 'results.table.top-speed',
      title: <FormattedMessage id={'results.table.top-speed'} />,
      dataIndex: ['top_speed'],
      align: 'center',
      sorter: (a: MotoGP.Result, b: MotoGP.Result) => a.top_speed - b.top_speed,
      render: (it: number) => (it ? <Typography.Text ellipsis>{`${it} km/h`}</Typography.Text> : '-'),
    },
    {
      key: 'results.table.gap',
      title: <FormattedMessage id={'results.table.gap'} />,
      dataIndex: ['gap'],
      render: (it: MotoGP.Result['gap']) =>
        it.first && <Typography.Text ellipsis>{[it.first, it.prev].filter(Boolean).join(' / ')}</Typography.Text>,
    },
  ]

  const columnsMobile: ColumnProps<MotoGP.Result>[] = [
    {
      key: 'results.table.position',
      dataIndex: ['position'],
      title: '#',
      align: 'center',
      width: 100,
      render: (position: number, item: MotoGP.Result) => (
        <Flex vertical gap={4}>
          <Typography.Text strong>{position}</Typography.Text>
          <Typography.Text>{item.time}</Typography.Text>
          {item.status === 'INSTND' ? (
            <Typography.Text
              type={item.position === 1 ? 'success' : Number.parseFloat(item.gap.first) === 0 ? 'secondary' : 'danger'}
            >
              {[item.position === 1 ? '' : '-', item.gap.first, 's'].join('')}
            </Typography.Text>
          ) : null}
        </Flex>
      ),
    },
    {
      key: 'results.table.rider',
      title: <FormattedMessage id={'results.table.rider'} />,
      dataIndex: ['rider'],
      render: (it: MotoGP.Result['rider'], item: MotoGP.Result) => (
        <Flex gap={4} vertical style={{ width: '100%' }}>
          <Flex gap={4} align={'center'}>
            <RiderNumber number={it.number} />
            <Link to={generatePath(routes.rider, { slug: slugify(it.full_name).toLowerCase() })}>{it.full_name}</Link>
          </Flex>
          <Typography.Text ellipsis strong>
            {item?.team?.name}
          </Typography.Text>
          <Typography.Text ellipsis>{item?.constructor?.name}</Typography.Text>
        </Flex>
      ),
    },
  ]

  return (
    <Flex vertical gap={16}>
      <Table
        size={'small'}
        style={{ overflowX: 'scroll' }}
        tableLayout={mobile ? 'fixed' : 'auto'}
        rowKey={(it) => it.id}
        pagination={{ hideOnSinglePage: true, defaultPageSize: 25 }}
        loading={isLoading}
        dataSource={data}
        columns={
          mobile
            ? columnsMobile
            : columnsDesktop.filter((it) => (Object.entries(teams).length < 5 ? it.key !== 'results.table.team' : it))
        }
      />
      {/*<Button onClick={() => saveData()}>Sync</Button>*/}
    </Flex>
  )
}

export { ResultTable }
